import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useNavigate, useParams } from 'react-router-dom';
import { buildSheinPath } from '@/shein-platform/constants/sheinRoutes';
import { useSheinShop } from '@/shein-platform/context/SheinShopContext';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import { extractSheinList, pickField } from '@/shein-platform/utils/sheinResponse';
import {
  detailBasicRows,
  detailPriceRows,
  detailReceiveRows,
  detailTimeRows,
  displayValue,
  formatTrackTime,
  listOrderProductSummary,
  normalizeImageUrl,
  salesStatusLabel,
  trackInfoList,
} from '@/shein-platform/modules/aftersales/salesOrderDisplay';
import SheinProductThumb from '@/shein-platform/modules/products/components/SheinProductThumb';
import { showToast } from '@/utils/toast';
import '@/styles/shein-platform/shein-product-ops.css';

function statusModifier(code) {
  const n = Number(code);
  if (n === 5) return 'success';
  if (n === 6 || n === 8 || n === 9) return 'failed';
  return 'pending';
}

/** 平台无轨迹（9999002）或同步跳过：对用户只展示友好空态，不抛原始 API 文案。 */
function isTrackUnavailable(snap) {
  if (!snap || typeof snap !== 'object') return false;
  if (snap.skipped) return true;
  const text = [snap.error, snap.reason, snap.message]
    .filter(Boolean)
    .map((v) => String(v))
    .join(' ');
  return /9999002|数据不存在|无轨迹|not\s*found|no\s*data/i.test(text);
}

function trackEmptyMessage(snap) {
  if (isTrackUnavailable(snap)) {
    return i18n.t('orderList.noTrackData', { ns: 'workbench' });
  }
  if (snap?.error) {
    return i18n.t('orderList.noTrack', { ns: 'workbench' });
  }
  return i18n.t('orderList.noTrackNodeShort', { ns: 'workbench' });
}

function extractDetailRecord(response, orderNo) {
  if (!response) return null;
  if (response.order && typeof response.order === 'object') {
    return response.order;
  }
  const list = extractSheinList(response);
  if (list.length) {
    const matched = list.find((row) => String(pickField(row, 'orderNo', 'orderNumber')) === String(orderNo));
    return matched || list[0];
  }
  if (typeof response === 'object' && !Array.isArray(response)) {
    if (pickField(response, 'orderNo', 'orderNumber') || response.orderGoodsInfoList) {
      return response;
    }
  }
  return null;
}

function hasRichDetail(record) {
  if (!record || typeof record !== 'object') return false;
  if (Array.isArray(record.orderGoodsInfoList)) return true;
  return Boolean(pickField(record, 'paymentTime', 'estimatedGrossIncome', 'packageWaybillList', 'salesSite'));
}

function goodsAttrText(goods) {
  const attrs = goods?.skuAttribute;
  if (!Array.isArray(attrs) || !attrs.length) return '';
  const byLang = new Map();
  attrs.forEach((a) => {
    const lang = String(a?.language || '').toUpperCase();
    const name = a?.attrName;
    if (!name) return;
    if (!byLang.has(lang)) byLang.set(lang, []);
    byLang.get(lang).push(name);
  });
  const cn = byLang.get('CN');
  const en = byLang.get('EN') || byLang.get('US');
  const pick = cn?.length ? cn : (en?.length ? en : [...byLang.values()][0]);
  return Array.isArray(pick) ? pick.join(' / ') : '';
}

function KvTable({ rows }) {
  const visible = (rows || []).filter((r) => r && r.label);
  if (!visible.length) {
    return <p className="spo-return-detail__empty">{i18n.t('orderList.emptyData', { ns: 'workbench' })}</p>;
  }
  return (
    <table className="spo-return-detail__kv">
      <tbody>
        {visible.map((row) => (
          <tr key={row.label}>
            <th scope="row">{row.label}</th>
            <td>{displayValue(row.value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TrackBlock({ snap }) {
  const tracks = trackInfoList(snap?.track || snap);
  if (!snap || isTrackUnavailable(snap) || snap?.error || !tracks.length) {
    return <div className="spo-order-track__empty">{trackEmptyMessage(snap)}</div>;
  }
  return (
    <div className="spo-order-track">
      {tracks.map((info, infoIdx) => (
        <div key={`${pickField(info, 'waybillNo') || infoIdx}`} className="spo-order-track__block">
          <div className="spo-order-track__meta">
            <span>{i18n.t('orderList.waybillPrefix', { ns: 'workbench' })}{pickField(info, 'waybillNo') || pickField(snap, 'waybillNo') || '—'}</span>
            <span>{i18n.t('orderList.carrierPrefix', { ns: 'workbench' })}{pickField(info, 'carrier') || '—'}</span>
            <span>{i18n.t('orderList.typePrefix', { ns: 'workbench' })}{pickField(info, 'waybillType') || '—'}</span>
          </div>
          <ol className="spo-order-track__timeline">
            {(Array.isArray(info.tracking) ? info.tracking : []).map((node, nodeIdx) => (
              <li key={`${nodeIdx}-${pickField(node, 'updateTimeMillis')}`}>
                <div className="spo-order-track__time">
                  {formatTrackTime(pickField(node, 'updateTimeMillis'))}
                </div>
                <div className="spo-order-track__node">
                  <strong>{pickField(node, 'nodeCodeName', 'nodeCode') || i18n.t('orderList.node', { ns: 'workbench' })}</strong>
                  <span>{pickField(node, 'description') || '—'}</span>
                </div>
              </li>
            ))}
          </ol>
        </div>
      ))}
    </div>
  );
}

export default function OrderDetailPage() {
  const { t } = useTranslation('workbench');
  const navigate = useNavigate();
  const { orderNo: rawNo } = useParams();
  const orderNo = decodeURIComponent(rawNo || '').trim();
  const { activeShopId, loading: shopLoading } = useSheinShop();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadDetail = useCallback(async ({ forceLive = false } = {}) => {
    if (!activeShopId || !orderNo) return;
    setLoading(true);
    setError('');
    try {
      if (!forceLive) {
        try {
          const cached = await sheinPlatformAPI.getCachedSheinOrder(activeShopId, orderNo);
          const record = extractDetailRecord(cached, orderNo);
          if (record && hasRichDetail(record)) {
            setDetail(record);
            return;
          }
        } catch {
          /* 本地无完整详情再拉平台 */
        }
      }

      const live = await sheinPlatformAPI.fetchSheinOrderDetails(activeShopId, {
        orderNoList: [orderNo],
      });
      const record = extractDetailRecord(live, orderNo);
      if (!record) {
        throw new Error(t('orderList.detailMissing'));
      }
      setDetail(record);
    } catch (e) {
      setDetail(null);
      setError(e?.message || t('orderList.detailLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [activeShopId, orderNo, t]);

  useEffect(() => {
    if (shopLoading) return;
    if (!activeShopId) {
      setDetail(null);
      setError(t('orderList.pickAuthorizedShop'));
      return;
    }
    if (!orderNo) {
      setError(t('orderList.invalidOrderNo'));
      return;
    }
    loadDetail();
  }, [activeShopId, orderNo, shopLoading, loadDetail, t]);

  const statusCode = pickField(detail, 'orderStatus');
  const statusLabel = salesStatusLabel(statusCode) || '—';
  const goodsList = Array.isArray(detail?.orderGoodsInfoList) ? detail.orderGoodsInfoList : [];
  const packages = Array.isArray(detail?.packageWaybillList) ? detail.packageWaybillList : [];
  const tracks = Array.isArray(detail?.ipeLogisticsTracks) ? detail.ipeLogisticsTracks : [];

  const handleBack = () => {
    navigate(buildSheinPath('orders'));
  };

  return (
    <div className="spo-return-detail" data-testid="shein-order-detail">
      <nav className="spo-return-detail__crumb" aria-label={t('orderList.crumb')}>
        <button type="button" className="spo-return-detail__crumb-link" onClick={handleBack}>
          {t('platformNav.orderList')}
        </button>
        <span className="spo-return-detail__crumb-sep">/</span>
        <span>{t('orderList.detailTitle')}</span>
      </nav>

      <header className="spo-return-detail__header">
        <div className="spo-return-detail__title-block">
          <h1 className="spo-return-detail__title">{t('orderList.detailTitle')}</h1>
          <div className="spo-return-detail__meta">
            <strong className="spo-return-detail__no">{orderNo || '—'}</strong>
            {detail ? (
              <span className={`spo-records__status spo-records__status--${statusModifier(statusCode)}`}>
                {statusLabel}
              </span>
            ) : null}
          </div>
        </div>
        <div className="spo-return-detail__actions">
          <button type="button" className="spo-btn spo-btn--secondary spo-btn--sm" onClick={handleBack}>
            {t('orderList.backToList')}
          </button>
          <button
            type="button"
            className="spo-btn spo-btn--primary spo-btn--sm"
            disabled={!activeShopId || !orderNo || loading}
            onClick={() => loadDetail({ forceLive: true }).then(() => showToast(t('orderList.refreshed'), 'success')).catch(() => {})}
          >
            {loading ? t('orderList.refreshing') : t('orderList.refreshFull')}
          </button>
        </div>
      </header>

      {error ? <div className="spo-ops-error" role="alert">{error}</div> : null}
      {loading && !detail ? (
        <div className="spo-return-detail__loading">{t('orderList.loadingDetail')}</div>
      ) : null}

      {detail ? (
        <>
          <section className="spo-return-detail__section">
            <h2 className="spo-return-detail__section-title">{t('orderList.basic')}</h2>
            <KvTable rows={detailBasicRows(detail)} />
          </section>

          <section className="spo-return-detail__section">
            <h2 className="spo-return-detail__section-title">{t('orderList.time')}</h2>
            <KvTable rows={detailTimeRows(detail)} />
          </section>

          <section className="spo-return-detail__section">
            <h2 className="spo-return-detail__section-title">{t('orderList.priceFees')}</h2>
            <KvTable rows={detailPriceRows(detail)} />
          </section>

          <section className="spo-return-detail__section">
            <h2 className="spo-return-detail__section-title">{t('orderList.receiveArea')}</h2>
            <KvTable rows={detailReceiveRows(detail)} />
          </section>

          <section className="spo-return-detail__section">
            <h2 className="spo-return-detail__section-title">{t('orderList.goodsCount', { count: goodsList.length })}</h2>
            {goodsList.length === 0 ? (
              <p className="spo-return-detail__empty">{t('orderList.noGoods')}</p>
            ) : (
              <div className="spo-drafts__table-wrap spo-ops-table-wrap">
                <table className="spo-publish__table spo-drafts__table spo-ops-table spo-ops-table--order-goods">
                  <thead>
                    <tr>
                      <th scope="col">{t('orderList.productInfo')}</th>
                      <th scope="col">SKC</th>
                      <th scope="col">{t('orderList.merchantSku')}</th>
                      <th scope="col">{t('orderList.supplierCode')}</th>
                      <th scope="col">{t('orderList.platformSku')}</th>
                      <th scope="col">goodsId</th>
                      <th scope="col">{t('orderList.spec')}</th>
                      <th scope="col">{t('orderList.goodsStatus')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {goodsList.map((goods, index) => {
                      const summary = listOrderProductSummary({ orderGoodsInfoList: [goods] });
                      const img = normalizeImageUrl(pickField(goods, 'spuPicURL', 'spuPicUrl', 'imageUrl'));
                      const statusCode = pickField(goods, 'newGoodsStatus', 'goodsStatus');
                      const attrText = goodsAttrText(goods);
                      return (
                        <tr key={String(pickField(goods, 'goodsId') || index)}>
                          <td className="spo-ops-td spo-ops-td--product">
                            <div className="spo-return-product">
                              {img ? (
                                <SheinProductThumb
                                  imageUrl={img}
                                  className="spo-return-product__img"
                                  placeholderClassName="spo-return-product__img spo-return-product__img--empty"
                                  alt={summary.title || t('orderList.productImage')}
                                />
                              ) : (
                                <div className="spo-return-product__img spo-return-product__img--empty" />
                              )}
                              <div className="spo-return-product__meta">
                                <div className="spo-return-product__title" title={summary.title || undefined}>
                                  {summary.title || '—'}
                                </div>
                                <div className="spo-return-product__sku">
                                  {t('orderList.spuPrefix')}{pickField(goods, 'spuName', 'goodsSn') || '—'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>{pickField(goods, 'skc') || '—'}</td>
                          <td>{pickField(goods, 'sellerSku', 'supplierSku') || '—'}</td>
                          <td>{pickField(goods, 'supplierCode', 'goodsSn') || '—'}</td>
                          <td>{pickField(goods, 'skuCode') || '—'}</td>
                          <td>{pickField(goods, 'goodsId') || '—'}</td>
                          <td title={attrText || undefined}>{attrText || '—'}</td>
                          <td>
                            <span className={`spo-records__status spo-records__status--${statusModifier(statusCode)}`}>
                              {salesStatusLabel(statusCode) || '—'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="spo-return-detail__section">
            <h2 className="spo-return-detail__section-title">{t('orderList.packageSection', { count: packages.length })}</h2>
            {packages.length === 0 ? (
              <p className="spo-return-detail__empty">{t('orderList.noPackages')}</p>
            ) : (
              packages.map((pkg, index) => {
                const packageNo = pickField(pkg, 'packageNo');
                const waybillNo = pickField(pkg, 'waybillNo', 'deliveryNo');
                const snap = tracks.find((t) => {
                  const p = String(pickField(t, 'packageNo') || '');
                  const w = String(pickField(t, 'waybillNo') || '');
                  if (packageNo && p === String(packageNo)) return true;
                  if (waybillNo && w === String(waybillNo)) return true;
                  return false;
                }) || tracks[index];
                return (
                  <div key={`${packageNo || waybillNo || index}`} className="spo-return-detail__section spo-return-detail__section--nested">
                    <KvTable
                      rows={[
                        { label: t('orderList.packageNo'), value: packageNo },
                        { label: t('orderList.waybillNo'), value: waybillNo },
                        { label: t('orderList.carrier'), value: pickField(pkg, 'carrier') },
                      ]}
                    />
                    <h3 className="spo-return-detail__section-title">{t('orderList.orderTrack')}</h3>
                    <TrackBlock snap={snap} />
                  </div>
                );
              })
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}
